import { DomainEventType } from '../enums';
import { CreatorGrowthLoopInputs } from './creator-growth-loop';
import { CreatorScoreModel } from './creator-score.model';

/**
 * Declarative mapping: domain events → growth loop inputs → score dimensions.
 * Definitions only — no event handlers or side effects.
 */
export interface EventLoopImpactMapping {
  readonly eventType: DomainEventType;
  readonly growthLoopInput?: keyof CreatorGrowthLoopInputs;
  readonly scoreImpact?: keyof CreatorScoreModel;
  readonly graphSignal?: 'followWeight' | 'engagementScore' | 'monetizationScore';
  readonly lifecycleHint?: string;
  readonly description: string;
}

export const EVENT_LOOP_MAPPINGS: readonly EventLoopImpactMapping[] = [
  {
    eventType: DomainEventType.POST_CREATED,
    growthLoopInput: 'content_created',
    scoreImpact: 'visibilityScore',
    lifecycleHint: 'active_creator',
    description: 'Publishing content increases feed visibility potential',
  },
  {
    eventType: DomainEventType.USER_FOLLOWED,
    growthLoopInput: 'follows_received',
    scoreImpact: 'visibilityScore',
    graphSignal: 'followWeight',
    description: 'Follow events increase creator graph weight and discovery signal',
  },
  {
    eventType: DomainEventType.PURCHASE_COMPLETED,
    growthLoopInput: 'purchases_generated',
    scoreImpact: 'monetizationScore',
    graphSignal: 'monetizationScore',
    lifecycleHint: 'monetizing_creator',
    description: 'Completed purchases increase monetization score and earnings loop input',
  },
  {
    eventType: DomainEventType.USER_CREATED,
    lifecycleHint: 'user',
    description: 'Account creation moves identity from visitor to user',
  },
  {
    eventType: DomainEventType.PRODUCT_CREATED,
    growthLoopInput: 'content_created',
    scoreImpact: 'monetizationScore',
    description: 'Product listing prepares monetization surface in feed (future)',
  },
  {
    eventType: DomainEventType.POST_DELETED,
    scoreImpact: 'visibilityScore',
    description: 'Content removal reduces visibility exposure (negative mapping placeholder)',
  },
  {
    eventType: DomainEventType.ADMIN_ACTION,
    scoreImpact: 'trustScore',
    description: 'Admin actions may affect trust score dimension (future enforcement)',
  },
];

/** Lookup mapping definition for a given domain event type. */
export function getEventLoopMapping(
  eventType: DomainEventType,
): EventLoopImpactMapping | undefined {
  return EVENT_LOOP_MAPPINGS.find((mapping) => mapping.eventType === eventType);
}
